import React, { useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import {
  useBodies,
  useWorldBodies,
  useJoints,
  useSites,
  useMeshes,
  useCameras,
  useActuators,
  useTendons,
  useEqualities,
  useSceneActions,
} from "../stores/sceneStore";
import { getEffectiveWorldBodies } from "../stores/sceneActions/bodyUtils";
import { useSelectionStore } from "../stores/selectionStore";
import { usePhysicsAuthoringLock } from "../utils/physicsGuards";
import { buildBodyParentMap } from "../utils/bodyHierarchy";
import { useViewerStore, useViewerActions } from "../stores/viewerStore";
import { clearSelectedVisualMeshes } from "../visual-mesh-multi-selection";
import { getTooltipPortalRoot, PORTAL_TOOLTIP_Z } from "../utils/panelZIndex";
import { buildSceneGraphBodiesSignature, collectDescendantBodyUuids } from "./scene-graph/helpers";
import { useSceneGraphMultiSelection } from "./scene-graph/useSceneGraphMultiSelection";
import { useSceneGraphContextMenu } from "./scene-graph/useSceneGraphContextMenu";
import { useSceneGraphContextMenuActions } from "./scene-graph/useSceneGraphContextMenuActions";
import { useSceneGraphReparentPick } from "./scene-graph/useSceneGraphReparentPick";
import { useSceneGraphViewport } from "./scene-graph/useSceneGraphViewport";
import { useSceneGraphHiddenTypes } from "./scene-graph/useSceneGraphHiddenTypes";
import { useSceneGraphMenuOptions } from "./scene-graph/useSceneGraphMenuOptions";
import {
  useBodyDepthMap,
  useGroupedSceneGraphParentOptions,
  usePrimaryRobotRootUuid,
  useWorldRootTargetBodyUuid,
} from "./scene-graph/useSceneGraphParentOptions";
import { useSceneGraphSelectionPruning } from "./scene-graph/useSceneGraphSelectionPruning";
import { useSceneGraphTooltip } from "./scene-graph/useSceneGraphTooltip";
import { useSceneGraphDiagramData } from "./scene-graph/useSceneGraphDiagramData";
import { useSceneGraphNodeInteractions } from "./scene-graph/useSceneGraphNodeInteractions";
import SceneGraphLegend from "./scene-graph/components/SceneGraphLegend";
import SceneGraphContextMenu from "./scene-graph/components/SceneGraphContextMenu";
import { SceneGraphContextMenuProvider } from "./scene-graph/components/SceneGraphContextMenuContext";
import SceneGraphSvgContent from "./scene-graph/components/SceneGraphSvgContent";
import SceneGraphAccessibleBodyTree from "./scene-graph/components/SceneGraphAccessibleBodyTree";
import SceneGraphViewportToolbar from "./scene-graph/components/SceneGraphViewportToolbar";

// component
export default function SceneGraphDiagram({ width = 600, height = 400, onOpenAssetLibrary }) {
  const bodies = useBodies();
  const sceneGraphBodiesSignature = useBodies(buildSceneGraphBodiesSignature);
  const worldBodies = useWorldBodies();

  const {
    bodyVisibility,
    meshVisibility,
    siteVisibility,
    collisionGeomVisibility,
    inertialVisibility,
    jointVizVisibility,
  } = useViewerStore(
    useShallow((s) => ({
      bodyVisibility: s.bodyVisibility,
      meshVisibility: s.meshVisibility,
      siteVisibility: s.siteVisibility,
      collisionGeomVisibility: s.collisionGeomVisibility,
      inertialVisibility: s.inertialVisibility,
      jointVizVisibility: s.jointVizVisibility,
    }))
  );
  const viewerActions = useViewerActions();
  const joints = useJoints();
  const sites = useSites();
  const meshes = useMeshes();
  const cameras = useCameras();
  const actuators = useActuators();
  const tendons = useTendons();
  const equalities = useEqualities();
  const sceneActions = useSceneActions();
  const isPhysicsRunning = usePhysicsAuthoringLock();

  const {
    selectedBody,
    selectedBodies,
    selectedJoint,
    selectedActuator,
    selectedSite,
    selectedGeom,
    selectedInertial,
    selectedMesh,
    selectedCamera,
    lastSelectionSource,
    selectJoint,
    selectSite,
    selectGeom,
    selectInertial,
    selectCamera,
    toggleBodyInSelection,
  } = useSelectionStore(
    useShallow((state) => ({
      selectedBody: state.selectedBody,
      selectedBodies: state.selectedBodies,
      selectedJoint: state.selectedJoint,
      selectedActuator: state.selectedActuator,
      selectedSite: state.selectedSite,
      selectedGeom: state.selectedGeom,
      selectedInertial: state.selectedInertial,
      selectedMesh: state.selectedMesh,
      selectedCamera: state.selectedCamera,
      lastSelectionSource: state.lastSelectionSource,
      selectJoint: state.selectJoint,
      selectSite: state.selectSite,
      selectGeom: state.selectGeom,
      selectInertial: state.selectInertial,
      selectCamera: state.selectCamera,
      toggleBodyInSelection: state.toggleBodyInSelection,
    }))
  );

  const primaryRobotRootUuid = usePrimaryRobotRootUuid(bodies, worldBodies);
  const { sceneGraphTooltip, showSceneGraphTooltip, hideSceneGraphTooltip } =
    useSceneGraphTooltip();
  const interactionLayerRef = useRef(null);
  const [sceneGraphSvgExporting, setSceneGraphSvgExporting] = React.useState(false);

  const bodyParentMap = useMemo(() => buildBodyParentMap(bodies || {}), [bodies]);

  // Multi-selection state (per-type Sets) and the effective-batch derivations
  // live in `useSceneGraphMultiSelection`. The hook merges each multi-selection
  // bag with its primary-selection counterpart so batch actions cover the
  // primary-selected item even when nothing else is multi-picked.
  const {
    multiSelectedMeshes,
    setMultiSelectedMeshes,
    multiSelectedGeoms,
    setMultiSelectedGeoms,
    multiSelectedSites,
    setMultiSelectedSites,
    multiSelectedInertials,
    setMultiSelectedInertials,
    multiSelectedJoints,
    setMultiSelectedJoints,
    effectiveBatchBodyUuids,
    effectiveBatchMeshIds,
    effectiveBatchGeomKeys,
    effectiveBatchSiteIds,
    effectiveBatchInertialKeys,
    effectiveBatchJointIds,
    effectiveBatchBodyUuidSet,
    effectiveBatchMeshIdSet,
    effectiveBatchGeomKeySet,
    effectiveBatchSiteIdSet,
    effectiveBatchInertialKeySet,
    effectiveBatchJointIdSet,
  } = useSceneGraphMultiSelection({
    bodies,
    selectedBodies,
    selectedMesh,
    selectedGeom,
    selectedSite,
    selectedInertial,
    selectedJoint,
  });
  // Context menu state, derived target ids, openers, and lifecycle effects
  // all live in `useSceneGraphContextMenu`. The hook also wires the
  // outside-click + Escape handlers and the mobile-edge-chrome sync.
  const {
    contextMenuType,
    contextMenuBodyUuid,
    contextMenuOpen,
    setContextMenuOpen,
    contextMenuPosition,
    reparentSubmenuOpen,
    deleteSubmenuOpen,
    addNewSubmenuOpen,
    setAddNewSubmenuOpen,
    duplicateSubmenuOpen,
    addJointKindSubmenuOpen,
    setAddJointKindSubmenuOpen,
    addGeomKindSubmenuOpen,
    setAddGeomKindSubmenuOpen,
    addVisualGeomKindSubmenuOpen,
    setAddVisualGeomKindSubmenuOpen,
    contextMenuRef,
    contextMenuBodyUuids,
    contextMenuBodyUuidSet,
    contextMenuMeshIds,
    contextMenuGeomKeys,
    contextMenuDirectCollisionGeomKey,
    contextMenuGeomSelectionLabel,
    contextMenuSiteIds,
    contextMenuInertialKeys,
    contextMenuInertialSourceBodyUuids,
    contextMenuJointIds,
    contextMenuCameraIds,
    closeContextMenu,
    closeContextSubmenus,
    setBodyActionSubmenuKey,
    setReparentOnlySubmenuKey,
    setJointActionSubmenuKey,
    openContextMenuForBody,
    openContextMenuForObject,
    openContextMenuForEmptySpace,
    openContextMenuForGeom,
    openContextMenuForSite,
    openContextMenuForInertial,
    openContextMenuForJoint,
    openContextMenuForCamera,
    clearContextMenuTargets,
  } = useSceneGraphContextMenu({
    bodies,
    isPhysicsRunning,
    interactionLayerRef,
    effectiveBatchBodyUuids,
    effectiveBatchBodyUuidSet,
    effectiveBatchMeshIds,
    effectiveBatchMeshIdSet,
    effectiveBatchGeomKeys,
    effectiveBatchGeomKeySet,
    effectiveBatchSiteIds,
    effectiveBatchSiteIdSet,
    effectiveBatchInertialKeys,
    effectiveBatchInertialKeySet,
    effectiveBatchJointIds,
    effectiveBatchJointIdSet,
  });
  const worldRootTargetBodyUuid = useWorldRootTargetBodyUuid({
    primaryRobotRootUuid,
    worldBodies,
    bodies,
  });
  const bodyDepthMap = useBodyDepthMap({ bodies, worldBodies, bodyParentMap });
  const isMeshParentAvailable = useCallback((body) => body?.uuid, []);
  const isInertialParentAvailable = useCallback(
    (body) => body?.uuid && (!body.inertia || contextMenuInertialSourceBodyUuids.has(body.uuid)),
    [contextMenuInertialSourceBodyUuids]
  );
  const groupedMeshParentOptions = useGroupedSceneGraphParentOptions({
    bodies,
    worldBodies,
    isBodyAvailable: isMeshParentAvailable,
  });
  const groupedInertialParentOptions = useGroupedSceneGraphParentOptions({
    bodies,
    worldBodies,
    isBodyAvailable: isInertialParentAvailable,
  });
  const { hiddenTypes, effectiveHiddenTypes, toggleType } = useSceneGraphHiddenTypes();

  const effectiveWorldBodies = useMemo(
    () => getEffectiveWorldBodies({ bodies, worldBodies }),
    [bodies, worldBodies]
  );

  useSceneGraphSelectionPruning({
    bodies,
    meshes,
    sites,
    joints,
    setMultiSelectedMeshes,
    setMultiSelectedGeoms,
    setMultiSelectedSites,
    setMultiSelectedInertials,
    setMultiSelectedJoints,
  });

  const clearBatchSelection = useCallback(() => {
    useSelectionStore.getState().setSelectedBodies([]);
    clearSelectedVisualMeshes();
    setMultiSelectedGeoms(new Set());
    setMultiSelectedSites(new Set());
    setMultiSelectedInertials(new Set());
    setMultiSelectedJoints(new Set());
    clearContextMenuTargets();
  }, [
    clearContextMenuTargets,
    setMultiSelectedGeoms,
    setMultiSelectedInertials,
    setMultiSelectedJoints,
    setMultiSelectedSites,
  ]);

  // Context-menu mutation handlers (inspect, add, duplicate, delete, move)
  // live in `useSceneGraphContextMenuActions`. The hook owns runGroupedBodyAction
  // and the per-entity handlers; we destructure everything the JSX wires up.
  const {
    runGroupedBodyAction,
    inspectContextMenuTarget,
    handleAddChildBody,
    handleAddRootBody,
    handleDuplicateBody,
    handleDuplicateCollision,
    handleAddJointToBody,
    handleAddSiteToBody,
    handleAddGeomToBody,
    handleAddVisualGeomToBody,
    handleAddActuatorForJoint,
    handleAddCameraToBody,
    handleAddFixedWorldCamera,
    handleAddConstraintForBody,
    handleAddConstraintForJoint,
    handleAddTendonForJoint,
    handleDeleteBodies,
    handleDeleteBodyObjects,
    handleDeleteMeshes,
    handleMoveMeshesToBody,
    handleDeleteGeoms,
    handleMoveGeomsToBody,
    handleDeleteSites,
    handleMoveSitesToBody,
    handleDeleteCameras,
    handleMoveCamerasToBody,
    handleDeleteInertials,
    handleMoveInertialsToBody,
    handleDeleteJoints,
    getJointReparentTargetDisabledReason,
    handleMoveJointsToBody,
  } = useSceneGraphContextMenuActions({
    isPhysicsRunning,
    bodies,
    joints,
    equalities,
    tendons,
    cameras,
    worldBodies,
    bodyDepthMap,
    worldRootTargetBodyUuid,
    sceneActions,
    closeContextMenu,
    clearBatchSelection,
    contextMenuType,
    contextMenuBodyUuid,
    contextMenuBodyUuids,
    contextMenuMeshIds,
    contextMenuGeomKeys,
    contextMenuSiteIds,
    contextMenuInertialKeys,
    contextMenuJointIds,
    contextMenuCameraIds,
  });

  // Reparent-pick gesture state, the moving-body collections, and the
  // drag handlers live in `useSceneGraphReparentPick`. It needs
  // runGroupedBodyAction from the actions hook above, so the call sits after
  // that destructure.
  const {
    reparentPick,
    reparentDragHoverBodyUuid,
    reparentDragGhost,
    effectiveMoveBodyUuids,
    reparentPickMoveSet,
    reparentPickInvalidParentUuids,
    handleMoveBodies,
    cancelReparentDrag,
    handleReparentDragPointerDown,
  } = useSceneGraphReparentPick({
    bodies,
    bodyParentMap,
    isPhysicsRunning,
    interactionLayerRef,
    runGroupedBodyAction,
    sceneActions,
    clearBatchSelection,
    closeContextMenu,
    contextMenuBodyUuids,
    contextMenuBodyUuidSet,
    effectiveBatchBodyUuids,
    effectiveBatchBodyUuidSet,
    effectiveBatchMeshIds,
    effectiveBatchMeshIdSet,
    effectiveBatchGeomKeys,
    effectiveBatchGeomKeySet,
    effectiveBatchSiteIds,
    effectiveBatchSiteIdSet,
    effectiveBatchInertialKeys,
    effectiveBatchInertialKeySet,
    effectiveBatchJointIds,
    effectiveBatchJointIdSet,
    handleMoveMeshesToBody,
    handleMoveGeomsToBody,
    handleMoveSitesToBody,
    handleMoveCamerasToBody,
    handleMoveInertialsToBody,
    handleMoveJointsToBody,
    getJointReparentTargetDisabledReason,
  });

  const invalidBatchParentUuids = useMemo(() => {
    // Exclude sources and their descendants so list targets cannot create cycles.
    return collectDescendantBodyUuids(effectiveMoveBodyUuids, bodies || {});
  }, [effectiveMoveBodyUuids, bodies]);

  const isMoveParentAvailable = useCallback(
    (body) => body?.uuid && !invalidBatchParentUuids.has(body.uuid),
    [invalidBatchParentUuids]
  );
  const groupedMoveParentOptions = useGroupedSceneGraphParentOptions({
    bodies,
    worldBodies,
    isBodyAvailable: isMoveParentAvailable,
  });

  const {
    getJointAddOptionsForBody,
    getPreferredTendonJointForBody,
    renderJointReparentBodyButton,
  } = useSceneGraphMenuOptions({
    bodies,
    joints,
    getJointReparentTargetDisabledReason,
    handleMoveJointsToBody,
  });

  const diagramData = useSceneGraphDiagramData({
    bodies,
    sceneGraphBodiesSignature,
    effectiveWorldBodies,
    joints,
    sites,
    meshes,
    cameras,
    actuators,
    tendons,
    equalities,
    effectiveHiddenTypes,
  });

  // Pan/zoom/viewport refs, viewBox, transforms, and pointer handlers live in
  // `useSceneGraphViewport`.
  const {
    svgRef,
    contentPanGroupRef,
    viewBox,
    contentPanGroupTransform,
    handleDiagramPointerDown,
    handleDiagramPointerMove,
    handleDiagramPointerUpOrCancel,
    handleDiagramLostPointerCapture,
    handleDiagramAuxClick,
    recenterSceneGraphView,
    downloadSceneGraphViewSvg,
  } = useSceneGraphViewport({
    width,
    height,
    diagramData,
    hasAnyMultiSelection:
      selectedBodies.length > 1 ||
      multiSelectedMeshes.size > 0 ||
      multiSelectedGeoms.size > 0 ||
      multiSelectedSites.size > 0 ||
      multiSelectedInertials.size > 0 ||
      multiSelectedJoints.size > 0,
    clearBatchSelection,
  });

  const handleDownloadSceneGraphSvg = useCallback(() => {
    if (sceneGraphSvgExporting) return;
    setSceneGraphSvgExporting(true);
    try {
      downloadSceneGraphViewSvg();
    } catch {
      // The viewport hook logs the export failure; keep the click handler settled.
    } finally {
      setSceneGraphSvgExporting(false);
    }
  }, [downloadSceneGraphViewSvg, sceneGraphSvgExporting]);

  const {
    getPrimitiveVisualGeomKey,
    selectBodyForSceneGraphTarget,
    openObjectPropertiesFromSceneGraphRoot,
    handleNodeClick,
    onSceneGraphEditorCurvePointerDown,
    onSceneGraphEditorCurveClick,
    getNodeFill,
  } = useSceneGraphNodeInteractions({
    bodies,
    selectedBody,
    selectedBodies,
    selectedJoint,
    selectedActuator,
    selectedSite,
    selectedGeom,
    selectedInertial,
    selectedMesh,
    selectedCamera,
    lastSelectionSource,
    selectJoint,
    selectSite,
    selectGeom,
    selectInertial,
    selectCamera,
    toggleBodyInSelection,
    multiSelectedMeshes,
    setMultiSelectedMeshes,
    multiSelectedGeoms,
    setMultiSelectedGeoms,
    multiSelectedSites,
    setMultiSelectedSites,
    multiSelectedInertials,
    setMultiSelectedInertials,
    multiSelectedJoints,
    setMultiSelectedJoints,
    clearBatchSelection,
    reparentPick,
  });

  // render
  const isEmpty = diagramData.nodes.length === 0;

  return (
    <div
      className="w-full h-full rounded-lg overflow-hidden select-none"
      style={{
        backgroundColor: "rgba(28, 28, 30, 0.98)",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      <style>{`
        @keyframes sceneNodeFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes reparentValidPulse {
          0%, 100% { opacity: 0.8; }
          50%      { opacity: 1; }
        }
      `}</style>
      <div
        ref={interactionLayerRef}
        className="relative w-full h-full select-none"
        style={{ WebkitUserSelect: "none", userSelect: "none" }}
        onContextMenu={(event) => {
          if (reparentPick) {
            event.preventDefault();
            event.stopPropagation();
            cancelReparentDrag();
            return;
          }
          const target = event.target;
          if (
            !(target instanceof Element) ||
            !target.closest("[data-scene-selectable-node='true']")
          ) {
            openContextMenuForEmptySpace(event);
          }
        }}
      >
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center text-sm panel-text-helper select-none">
            <div>No scene elements loaded</div>
            {onOpenAssetLibrary ? (
              <div className="max-w-sm">
                Open the{" "}
                <button
                  type="button"
                  className="text-gray-300 underline decoration-gray-500 underline-offset-4 transition-colors hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                  onClick={onOpenAssetLibrary}
                >
                  Library
                </button>{" "}
                to add prebuilt robots and objects to the scene
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <SceneGraphViewportToolbar
              exporting={sceneGraphSvgExporting}
              onRecenter={recenterSceneGraphView}
              onDownloadSvg={handleDownloadSceneGraphSvg}
            />
            <SceneGraphAccessibleBodyTree
              bodies={bodies}
              effectiveWorldBodies={effectiveWorldBodies}
              selectedBody={selectedBody}
              selectBodyForSceneGraphTarget={selectBodyForSceneGraphTarget}
            />
            <svg
              ref={svgRef}
              width={width}
              height={height}
              viewBox={viewBox}
              onPointerDown={handleDiagramPointerDown}
              onPointerMove={handleDiagramPointerMove}
              onPointerUp={handleDiagramPointerUpOrCancel}
              onPointerCancel={handleDiagramPointerUpOrCancel}
              onLostPointerCapture={handleDiagramLostPointerCapture}
              onAuxClick={handleDiagramAuxClick}
              className="w-full h-full touch-none select-none"
              style={{
                cursor: "grab",
                touchAction: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              }}
            >
              <SceneGraphSvgContent
                diagramData={diagramData}
                contentPanGroupRef={contentPanGroupRef}
                contentPanGroupTransform={contentPanGroupTransform}
                effectiveHiddenTypes={effectiveHiddenTypes}
                reparentPick={reparentPick}
                cancelReparentDrag={cancelReparentDrag}
                reparentDragHoverBodyUuid={reparentDragHoverBodyUuid}
                reparentDragGhost={reparentDragGhost}
                reparentPickMoveSet={reparentPickMoveSet}
                reparentPickInvalidParentUuids={reparentPickInvalidParentUuids}
                selectedBody={selectedBody}
                lastSelectionSource={lastSelectionSource}
                openObjectPropertiesFromSceneGraphRoot={openObjectPropertiesFromSceneGraphRoot}
                showSceneGraphTooltip={showSceneGraphTooltip}
                hideSceneGraphTooltip={hideSceneGraphTooltip}
                onSceneGraphEditorCurvePointerDown={onSceneGraphEditorCurvePointerDown}
                onSceneGraphEditorCurveClick={onSceneGraphEditorCurveClick}
                openContextMenuForObject={openContextMenuForObject}
                selectBodyForSceneGraphTarget={selectBodyForSceneGraphTarget}
                getNodeFill={getNodeFill}
                getPrimitiveVisualGeomKey={getPrimitiveVisualGeomKey}
                openContextMenuForBody={openContextMenuForBody}
                openContextMenuForJoint={openContextMenuForJoint}
                openContextMenuForGeom={openContextMenuForGeom}
                openContextMenuForSite={openContextMenuForSite}
                openContextMenuForCamera={openContextMenuForCamera}
                openContextMenuForInertial={openContextMenuForInertial}
                handleNodeClick={handleNodeClick}
                handleReparentDragPointerDown={handleReparentDragPointerDown}
              />
            </svg>
          </>
        )}

        {/* legend */}
        {!isEmpty && (
          <SceneGraphLegend
            hiddenTypes={hiddenTypes}
            effectiveHiddenTypes={effectiveHiddenTypes}
            toggleType={toggleType}
          />
        )}

        <SceneGraphContextMenuProvider
          value={{
            state: {
              contextMenuOpen,
              contextMenuType,
              contextMenuBodyUuid,
              contextMenuBodyUuids,
              contextMenuJointIds,
              contextMenuMeshIds,
              contextMenuGeomKeys,
              contextMenuSiteIds,
              contextMenuCameraIds,
              contextMenuInertialKeys,
              contextMenuRef,
              contextMenuPosition,
              contextMenuGeomSelectionLabel,
              contextMenuDirectCollisionGeomKey,
              contextMenuInertialSourceBodyUuids,
            },
            submenus: {
              deleteSubmenuOpen,
              duplicateSubmenuOpen,
              addNewSubmenuOpen,
              setAddNewSubmenuOpen,
              addJointKindSubmenuOpen,
              setAddJointKindSubmenuOpen,
              addGeomKindSubmenuOpen,
              setAddGeomKindSubmenuOpen,
              addVisualGeomKindSubmenuOpen,
              setAddVisualGeomKindSubmenuOpen,
              reparentSubmenuOpen,
              setBodyActionSubmenuKey,
              setJointActionSubmenuKey,
              setReparentOnlySubmenuKey,
              closeContextSubmenus,
              setContextMenuOpen,
            },
            scene: {
              isPhysicsRunning,
              worldRootTargetBodyUuid,
              effectiveMoveBodyUuids,
              invalidBatchParentUuids,
              groupedMoveParentOptions,
              groupedMeshParentOptions,
              groupedInertialParentOptions,
              bodies,
            },
            visibility: {
              viewerActions,
              bodyVisibility,
              meshVisibility,
              siteVisibility,
              collisionGeomVisibility,
              inertialVisibility,
              jointVizVisibility,
            },
            actions: {
              inspectContextMenuTarget,
              handleAddRootBody,
              handleAddChildBody,
              handleAddJointToBody,
              handleAddSiteToBody,
              handleAddGeomToBody,
              handleAddVisualGeomToBody,
              handleAddConstraintForBody,
              handleAddConstraintForJoint,
              handleAddTendonForJoint,
              handleAddCameraToBody,
              handleAddFixedWorldCamera,
              handleAddActuatorForJoint,
              handleDuplicateBody,
              handleDuplicateCollision,
              handleDeleteBodyObjects,
              handleDeleteBodies,
              handleDeleteJoints,
              handleDeleteMeshes,
              handleDeleteGeoms,
              handleDeleteSites,
              handleDeleteCameras,
              handleDeleteInertials,
              handleMoveBodies,
              handleMoveJointsToBody,
              handleMoveMeshesToBody,
              handleMoveGeomsToBody,
              handleMoveSitesToBody,
              handleMoveCamerasToBody,
              handleMoveInertialsToBody,
            },
            options: {
              getJointAddOptionsForBody,
              getPreferredTendonJointForBody,
              renderJointReparentBodyButton,
            },
          }}
        >
          <SceneGraphContextMenu />
        </SceneGraphContextMenuProvider>
        {sceneGraphTooltip
          ? createPortal(
              <span
                role="tooltip"
                className="pointer-events-none fixed max-w-xs rounded-md border border-gray-700 bg-gray-900 px-2 py-1.5 text-xs text-gray-100 shadow-lg"
                style={{
                  left: `${sceneGraphTooltip.left}px`,
                  top: `${sceneGraphTooltip.top}px`,
                  transform: "translate(-50%, -100%)",
                  zIndex: PORTAL_TOOLTIP_Z,
                }}
              >
                {sceneGraphTooltip.content}
              </span>,
              getTooltipPortalRoot() ?? document.body
            )
          : null}
      </div>
    </div>
  );
}
